import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api } from '../../api/repository'
import {
  Button,
  EmptyState,
  ItemCell,
  KeyValueRow,
  Panel,
  ScreenHeader,
  Section,
  SectionHeader
} from '../../ui/primitives'
import { useRemote } from '../../state/useRemote'
import styles from './catalog.module.css'

/**
 * Страница коллекции: чем она является и что в ней собирать.
 *
 * Предметы грузятся отдельно от самой коллекции: чек-листы наполняются
 * позже каталога, и пустой список не должен мешать показать карточку.
 */
export function CollectionScreen(): ReactNode {
  const { slug = '' } = useParams()

  const collection = useRemote(() => api.collection(slug), [slug])
  const items = useRemote(() => api.collectionItems(slug), [slug])

  if (collection.isLoading) {
    return <span className="label">Загрузка…</span>
  }

  if (collection.data === null) {
    return (
      <Section gap={20}>
        <ScreenHeader title="Коллекция" />
        <div className={styles.failure}>
          {collection.error ?? 'Коллекция не найдена'}
        </div>
        <div>
          <Link to="/collections">
            <Button>Ко всем коллекциям</Button>
          </Link>
        </div>
      </Section>
    )
  }

  const data = collection.data

  return (
    <div className={styles.detail}>
      <ScreenHeader
        title={data.title}
        subtitle={data.category}
        action={
          <Link to="/collections">
            <Button>К каталогу</Button>
          </Link>
        }
      />

      <div className={styles.facts}>
        <KeyValueRow
          label="Год выпуска"
          value={data.year === null ? '—' : String(data.year)}
        />
        <KeyValueRow label="Издатель" value={data.publisher ?? '—'} />
        <KeyValueRow label="Категория" value={data.category} />
        <KeyValueRow
          label="В наборе"
          value={String(data.itemsTotal)}
          emphasis
        />
      </div>

      <Section>
        <SectionHeader
          title="Чек-лист"
          trailing={
            items.data === null
              ? '—'
              : `${items.data.length} из ${data.itemsTotal}`
          }
        />
        {items.isLoading ? (
          <span className="label">Загрузка…</span>
        ) : items.error !== null ? (
          <div className={styles.failure}>{items.error}</div>
        ) : items.data !== null && items.data.length > 0 ? (
          <div className={styles.checklist}>
            {items.data.map((item) => (
              <ItemCell key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <>
            <Panel>
              <EmptyState text="Чек-лист этой коллекции ещё не заведён" />
            </Panel>
            <p className={styles.note}>
              В каталог перенесены сами коллекции: название, год,
              издатель и размер набора. Номера и названия предметов —
              следующий шаг наполнения, до тех пор прогресс считается от
              заявленных {data.itemsTotal} предметов.
            </p>
          </>
        )}
      </Section>
    </div>
  )
}
